import { t as tr } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type DirectoryUser, type Rate } from '../api.js'
import { atLeast, useAuth } from '../auth.js'

/** People → cost rates for one person (managers read, admins write). */
export function CostRatesEditor({ user, onClose }: { user: DirectoryUser; onClose: () => void }) {
  const { user: me } = useAuth()
  const canWrite = atLeast(me, 'admin')
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['cost-rates', user.id], queryFn: () => api<{ rates: Rate[] }>(`/users/${user.id}/cost-rates`) })
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [err, setErr] = useState<string | null>(null)
  const add = useMutation({ mutationFn: () => api(`/users/${user.id}/cost-rates`, { method: 'POST', json: { effectiveFrom: from, amount: Number(amount), currency } }), onSuccess: () => { setAmount(''); void qc.invalidateQueries({ queryKey: ['cost-rates', user.id] }) }, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not add') })
  const del = useMutation({ mutationFn: (id: string) => api(`/cost-rates/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-rates', user.id] }) })
  return (
    <section className="card stack editor">
      <div className="row between"><h2 style={{ margin: 0 }}>Cost rate — {user.displayName}</h2><button className="link" onClick={onClose}>{tr("Close")}</button></div>
      <p className="small muted">What this person costs ADKOM per hour, independent of project. Effective-dated; the rate in force on an entry's date applies. Visible to managers and admins only; audit-logged.</p>
      {q.data && (q.data.rates.length === 0 ? <p className="muted small">{tr("No cost rate yet.")}</p> : (
        <ul className="plain">{q.data.rates.map((r) => <li key={r.id}>{tr("from")} <span className="mono">{r.effectiveFrom}</span>: <strong>{r.amount.toFixed(2)} {r.currency}</strong>/h{r.current ? <span className="tag">{tr("current")}</span> : ''} {canWrite && <button className="link danger" onClick={() => del.mutate(r.id)}>remove</button>}</li>)}</ul>
      ))}
      {canWrite && (
        <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); add.mutate() }}>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          <input type="number" step="0.01" min="0" placeholder={tr("cost / hour")} value={amount} onChange={(e) => setAmount(e.target.value)} required style={{ width: '8rem' }} />
          <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} style={{ width: '4.5rem' }} />
          <button className="button">{tr("Add")}</button>
        </form>
      )}
      {err && <p className="bad small">{err}</p>}
    </section>
  )
}
